import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import queryString from 'query-string'
import { Loading3QuartersOutlined } from '@ant-design/icons'

import { useMountedRef } from '../../../_utils/use-mounted-ref'
import { useService } from '../../../hooks/use-service'
import { DomainIntegrationService } from '../../../services'
import { useLoadableDataState } from '../../../hooks/use-loadable-data-state'
import { simpleNotification } from '../../../_utils/utils'
import { IntegrationProps } from '../interfaces'
import { handleResponseErrorMessage } from '../../../_utils/response-error-utils'
import { DomainIntegrationsManagerContext } from '../context'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PlatformIntegrationCard } from '../../platform-integrations/platform-integration-card/platform-integration-card'
import { GetSlackDomainIntegrationResponse, UpdateDomainIntegrationRequest } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationsManagerDispatchContext } from '../reducer'
import { Descriptions, Result, Switch } from 'antd'
import { PlatformIntegrationModal } from '../../platform-integrations/platform-integration-modal/platform-integration-modal'
import { SlackOAuthDialogState } from '../interfaces'

const SlackIntegration = (props: IntegrationProps<GetSlackDomainIntegrationResponse>) => {
    const { loading, domain } = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)

    const mode = props.mode
    const integration = mode === 'edit' ? props.integration : null

    const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
    const [toggleActionLoading, setToggleActionLoading] = useState(false)
    const [oauthDialog, setOauthDialog] = useState<SlackOAuthDialogState>({ status: 'hidden' })

    const history = useHistory()
    const runIfMounted = useMountedRef()[1]
    const [value, setValue] = useLoadableDataState({
        loading: false,
        data: integration,
    })
    const svc = useService<DomainIntegrationService>(DomainIntegrationService)

    const qs = queryString.parse(location.search)

    const closeOauthDialog = () => {
        history.replace({ hash: 'integrations', search: '' })
        setOauthDialog({ status: 'hidden' })
    }

    React.useEffect(() => {
        const processOauthCallback = async (domainId: number, _oauthState: string) => {
            setPrimaryActionLoading(true)

            setValue({
                loading: true,
                data: {
                    ...(value.data ?? ({} as any)),
                    isActive: true,
                    data: {
                        teamName: qs.team_name,
                        incomingWebhook: {
                            channel: qs.channel,
                        },
                    },
                },
            })

            setOauthDialog({ status: 'finalizing' })

            const res = await svc.completeIntegrationSetup(domainId, DomainIntegrationProviders.SLACK, qs, {
                cancellationKey: 'slack-integration',
            })
            setPrimaryActionLoading(false)

            if (!res.ok) {
                setOauthDialog({ status: 'failed' })
                return
            }

            setOauthDialog({
                status: 'completed',
                teamName: res.data?.data?.team_name ?? '',
                channel: res.data?.data?.incoming_webhook?.channel ?? '',
            })
        }

        if (mode === 'add' && domain?.id && qs.provider === DomainIntegrationProviders.SLACK && qs.state) {
            processOauthCallback(domain.id, qs.state.toString())
        }
    }, [mode, domain?.id, qs.state, qs.provider])

    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [removeActionLoading, setRemoveActionLoading] = useState(false)

    const createIntegration = async () => {
        if (loading) return
        setPrimaryActionLoading(true)

        const redirectUrl = await svc.fetchIntegrationRedirectUrl('slack', domain.id)

        if (!redirectUrl.ok) {
            const err = redirectUrl.error as any
            handleResponseErrorMessage(err)
        } else {
            const { authUrl } = redirectUrl.data!
            window.location.href = authUrl
        }
    }

    const removeIntegration = () => {
        if (loading || !integration) return
        setRemoveDialogOpen(true)
    }

    const confirmRemoveIntegration = async () => {
        if (!integration || !domain) return
        const confirmedIntegration = integration
        setRemoveActionLoading(true)

        const res = await svc.removeIntegration(domain.id, value.data!.id!)

        setRemoveActionLoading(false)

        if (res.ok) {
            setRemoveDialogOpen(false)
            simpleNotification('success', 'Integration successfully removed.')
            dispatch({ type: 'rem-integration', integration: confirmedIntegration })
        } else {
            setValue({ ...value, loading: false })
        }
    }

    const cancelRemoveIntegration = () => {
        setPrimaryActionLoading(false)
        setRemoveDialogOpen(false)
        setValue({ ...value, loading: false })
    }

    const toggleIntegration = async (enabled: boolean) => {
        if (loading || !integration) return
        setToggleActionLoading(true)

        const update = new UpdateDomainIntegrationRequest()
        update.id = integration.id
        update.is_active = enabled

        // @ts-ignore - @fixme - change updateById signature
        const res = await svc.updateById(domain.id, update)

        runIfMounted(() => {
            setToggleActionLoading(false)

            if (!res.ok || !res.data) {
                if (!res.cancelled) {
                    console.warn(res.error)

                    simpleNotification(
                        'error',
                        enabled ? 'Integration could not be enabled.' : 'Integration could not be disabled.',
                    )
                }

                return
            }

            simpleNotification(
                'success',
                enabled ? 'Integration successfully enabled.' : 'Integration successfully disabled.',
            )

            dispatch({ type: 'set-integration', integration: res.data })
        })
    }

    const slackLogo = <span className="slack-logo-color" />

    return (
        <>
            <PlatformIntegrationCard
                loading={loading}
                className={props.className}
                provider={DomainIntegrationProviders.SLACK}
                logo={slackLogo}
                mode={mode}
                isActive={integration?.is_active}
                actions={{
                    primary: {
                        loading: primaryActionLoading,
                        onClick: () => {
                            if (!integration) {
                                createIntegration()
                            } else {
                                removeIntegration()
                            }
                        },
                    },
                }}
            >
                {integration && (
                    <Descriptions layout="horizontal" column={1}>
                        <Descriptions.Item className="slack-enabled-toggle" label="Enabled">
                            <Switch
                                size="small"
                                loading={toggleActionLoading}
                                checked={integration.is_active}
                                onChange={toggleIntegration}
                                aria-label="Enable Slack integration"
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="Team Name">{integration.data.team_name}</Descriptions.Item>
                        <Descriptions.Item label="Channel">
                            {integration.data.incoming_webhook.channel}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </PlatformIntegrationCard>

            {/* OAuth completion modal — finalizing / completed / failed states */}
            <PlatformIntegrationModal
                className="slack-oauth-dialog"
                open={oauthDialog.status !== 'hidden'}
                providerLogo={slackLogo}
                title={
                    oauthDialog.status === 'finalizing'
                        ? 'Integration Finalizing'
                        : oauthDialog.status === 'completed'
                        ? 'Integration Completed'
                        : 'Integration Failed'
                }
                okText={oauthDialog.status === 'finalizing' ? undefined : 'Close'}
                okButtonProps={{ disabled: oauthDialog.status === 'finalizing' }}
                cancelButtonProps={{ style: { display: 'none' } }}
                onOk={() => {
                    if (oauthDialog.status === 'completed') {
                        simpleNotification('success', 'Integration successfully completed.')
                        dispatch({ type: 'request-integrations-reload' })
                    }
                    closeOauthDialog()
                }}
                onCancel={closeOauthDialog}
            >
                {oauthDialog.status === 'finalizing' && (
                    <Result icon={<Loading3QuartersOutlined spin={true} />} title={false} />
                )}
                {oauthDialog.status === 'completed' && (
                    <Result
                        status="success"
                        title={false}
                        subTitle={`Team name: ${oauthDialog.teamName}; Channel: ${oauthDialog.channel}`}
                    />
                )}
                {oauthDialog.status === 'failed' && (
                    <Result
                        status="warning"
                        title={false}
                        subTitle="An error occurred during your integration and we were unable to complete the setup."
                    />
                )}
            </PlatformIntegrationModal>

            {/* Remove integration confirmation modal */}
            <PlatformIntegrationModal
                className="slack-remove-integration-dialog"
                open={removeDialogOpen}
                providerLogo={slackLogo}
                title="Remove Integration"
                okText="Remove"
                okButtonProps={{ type: 'remove', loading: removeActionLoading }}
                onOk={confirmRemoveIntegration}
                onCancel={cancelRemoveIntegration}
            >
                {integration && (() => {
                    const { channel, configuration_url } = integration.data.incoming_webhook
                    return (
                        <>
                            <p>
                                Performing this step will remove the Slack integration for channel <b>{channel}</b> from
                                the platform. To completely remove Slack access visit the following URL:
                            </p>
                            <p className="config-url">
                                <a href={configuration_url} target="_blank" rel="noreferrer">
                                    {configuration_url}
                                </a>
                            </p>
                            <p>
                                Access can be modified from the <b>Authorizations</b> panel, or completely removed by
                                using the <b>Remove App</b> button, on the <b>Configuration</b> tab.
                            </p>
                        </>
                    )
                })()}
            </PlatformIntegrationModal>
        </>
    )
}

export default SlackIntegration
